class MobileStickyAtc {
  constructor(root = document) {
    this.root = root;
    this.bar = null;
    this.trigger = null;
    this.label = null;
    this.submitInput = null;
    this.productFormEl = null;
    this.formObserver = null;
    this.scrollTicking = false;

    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleFormMutations = this.handleFormMutations.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);

    this.init();
  }

  init() {
    this.bar = this.root.querySelector("[data-mobile-sticky-atc]");
    if (!this.bar) return;

    this.trigger = this.bar.querySelector("[data-mobile-sticky-atc-trigger]");
    this.label = this.bar.querySelector("[data-mobile-sticky-atc-label]");
    if (!this.trigger || !this.label) return;

    this.defaultLabel = this.label.textContent.trim();

    this.trigger.addEventListener("click", this.handleTriggerClick);
    window.addEventListener("scroll", this.handleScroll, { passive: true });
    window.addEventListener("resize", this.handleResize, { passive: true });

    this.bindToProductForm();
    this.syncState();
    this.syncVisibility();
  }

  bindToProductForm() {
    const nextSubmitInput = this.findSubmitInput();
    if (!nextSubmitInput) {
      this.disconnectFormObserver();
      this.submitInput = null;
      this.productFormEl = null;
      return;
    }

    const nextProductForm = nextSubmitInput.closest("product-form") || null;
    const hasChanged =
      this.submitInput !== nextSubmitInput || this.productFormEl !== nextProductForm;
    if (!hasChanged) return;

    this.disconnectFormObserver();
    this.submitInput = nextSubmitInput;
    this.productFormEl = nextProductForm;

    if (this.productFormEl) {
      this.formObserver = new MutationObserver(this.handleFormMutations);
      this.formObserver.observe(this.productFormEl, {
        attributes: true,
        subtree: true,
        childList: true,
        attributeFilter: ["disabled", "value", "class"],
      });
    }
  }

  disconnectFormObserver() {
    if (!this.formObserver) return;
    this.formObserver.disconnect();
    this.formObserver = null;
  }

  findSubmitInput() {
    const productRoot = document.querySelector("#main-product-container");
    if (!productRoot) return null;

    const inputs = Array.from(productRoot.querySelectorAll("product-form [add-to-cart]"));
    if (!inputs.length) return null;

    return inputs[0];
  }

  handleFormMutations() {
    this.bindToProductForm();
    this.syncState();
    this.syncVisibility();
  }

  getSubmitLabel() {
    if (!this.submitInput) return this.defaultLabel;

    const valueText = (
      this.submitInput.value ||
      this.submitInput.getAttribute("value") ||
      ""
    ).trim();
    if (valueText) return valueText;

    return this.defaultLabel;
  }

  syncState() {
    this.bindToProductForm();

    const hasSubmit = !!this.submitInput;
    const isDisabled = !hasSubmit || this.submitInput.disabled;

    this.trigger.disabled = isDisabled;
    this.label.textContent = this.getSubmitLabel();

    this.bar.classList.toggle("is-unavailable", isDisabled);
  }

  isSubmitScrolledPast() {
    if (!this.submitInput || typeof this.submitInput.getBoundingClientRect !== "function")
      return false;
    const rect = this.submitInput.getBoundingClientRect();
    return rect.bottom <= 0;
  }

  syncVisibility() {
    const hasSubmit = !!this.submitInput;
    const shouldShow = hasSubmit && this.isSubmitScrolledPast();
    this.bar.classList.toggle("is-visible", shouldShow);
  }

  handleScroll() {
    if (this.scrollTicking) return;
    this.scrollTicking = true;
    window.requestAnimationFrame(() => {
      this.scrollTicking = false;
      this.syncVisibility();
    });
  }

  handleResize() {
    this.syncVisibility();
  }

  handleTriggerClick(event) {
    event.preventDefault();

    this.bindToProductForm();
    this.syncState();

    if (!this.submitInput || this.submitInput.disabled) return;

    const nativeForm = this.submitInput.form;
    if (nativeForm && typeof nativeForm.requestSubmit === "function") {
      nativeForm.requestSubmit(this.submitInput);
      return;
    }

    this.submitInput.click();
  }
}

const bootMobileStickyAtc = (root = document) => {
  if (!root) return;

  const bars = root.querySelectorAll("[data-mobile-sticky-atc]");
  if (!bars.length) return;

  bars.forEach((bar) => {
    if (bar.dataset.mobileStickyAtcBound === "true") return;
    bar.dataset.mobileStickyAtcBound = "true";
    new MobileStickyAtc(document);
  });
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => bootMobileStickyAtc());
} else {
  bootMobileStickyAtc();
}

document.addEventListener("shopify:section:load", (event) => {
  bootMobileStickyAtc(event?.target || document);
});

export { MobileStickyAtc };
